import { queryOptions, useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import ApiDoctorConversationsManager from 'services/api/ApiDoctorConversationsManager';
import { selectCurrentUser } from 'store/users/selectors';

/**
 * Single source of truth for the patient history query definition.
 * Import this wherever you need to read, fetch, or invalidate patient history
 * so that the key, queryFn, and staleTime are always in sync.
 */
export const patientHistoryQueryOptions = (patientId: string) =>
  queryOptions({
    queryKey: ['patientHistory', patientId],
    queryFn: () => ApiDoctorConversationsManager.getPatientConversationHistory(patientId),
    staleTime: 1000 * 10, // 10 seconds
  });

/**
 * Query options for the messages of a single historic (read-only) conversation.
 * Only caregivers access this page, so the doctor endpoint is always used.
 */
export const historicConversationMessagesQueryOptions = (conversationId: string) =>
  queryOptions({
    queryKey: ['historicConversationMessages', conversationId] as const,
    queryFn: () => ApiDoctorConversationsManager.getDoctorConversationMessageList({ conversationId }),
  });

/**
 * Loads (and caches via TanStack Query) the conversation history for a given
 * patient. Only caregivers can call the underlying endpoint, so the hook is a
 * no-op when invoked by a patient.
 *
 * Designed so it can be mounted from several places at once without flooding
 * the backend:
 * - concurrent mounts for the same patientId share one in-flight request
 *   automatically (TanStack Query deduplication);
 * - subsequent mounts reuse the cached result (staleTime: Infinity) without
 *   re-fetching unless the cache is invalidated;
 * - a websocket reconnect invalidates the cache to re-run the fetch (gap
 *   recovery), mirroring the conversations-list bootstrap.
 */
export default function useLoadPatientHistory(patientId: string) {
  const currentUser = useSelector(selectCurrentUser);

  const enabled = !!currentUser?.is_caregiver && !!patientId;

  const { data, status, refetch } = useQuery({
    ...patientHistoryQueryOptions(patientId),
    enabled,
  });

  return {
    loading: status === 'pending',
    history: data ?? [],
    status,
    refresh: () => refetch(),
  };
}

/**
 * Returns the count of conversations in the patient history cache, plus a
 * loading flag that is true while the initial fetch is in progress. Callers
 * should show a spinner while loading instead of a stale/zero count.
 */
export function usePatientHistoryCount(patientId: string): { count: number; loading: boolean } {
  const { data, status } = useQuery({
    ...patientHistoryQueryOptions(patientId),
    enabled: false,
  });

  return {
    count: data?.length ?? 0,
    loading: status === 'pending',
  };
}
